import {
    DynamicDataType,
    DynamicSetting,
    DynamicValidationRule,
    DynamicValidationRuleType,
    DisplayValueListValidationRule,
    PrecisionValidationRule,
    RangeValidationRule,
} from '../models/dynamic-section-models';
import { ValidationRule } from '../models/validator-models';
import { min, max, pattern } from './validator-builders';

/**
 * Maximum value for UInt16 data type (2^16 - 1)
 */
const UINT16_MAX_VALUE = 65535;

const NONE_UNIT_VALUE = 'None';

const enum UnitKeyword {
    MM = 'mm',
    MILLIMETER = 'millimeter',
    MILLIMETERS = 'millimeters',

    IN = 'in',
    INCH = 'inch',
    INCHES = 'inches',

    FT = 'ft',
    FOOT = 'foot',
    FEET = 'feet',

    CF = 'cf',
    FT3 = 'ft3',
    CUBIC_FEET = 'cubicfeet',

    MG = 'mg',
    MILLION_GALLONS = 'milliongallons',
    MIL_GAL = 'milgal',

    M_PER_S = 'm/s',
    MPS = 'mps',
    METERS_PER_SECOND = 'meterspersecond',

    FT_PER_S = 'ft/s',
    FPS = 'fps',
    FEET_PER_SECOND = 'feetpersecond',

    L_PER_S = 'l/s',
    LPS = 'lps',
    LITERS_PER_SECOND = 'literspersecond',

    CFS = 'cfs',
    CUBIC_FEET_PER_SECOND = 'cubicfeetpersecond',
    CUFT_PER_S = 'cuft/s',

    MGD = 'mgd',
    MILLION_GALLONS_PER_DAY = 'milliongallonsperday',
    MIL_GAL_PER_DAY = 'milgal/day',
}

const enum UnitPrecision {
    DEPTH_METRIC = 1,
    DEPTH_US = 2,
    DISTANCE_US = 2,
    VOLUME_CUBIC_FEET = 0,
    VOLUME_MILLION_GALLONS = 3,
    VELOCITY = 2,
    FLOW_METRIC = 1,
    FLOW_US = 3,
}

const UNIT_PRECISION_MAP: ReadonlyMap<string, number> = new Map([
    [UnitKeyword.MM, UnitPrecision.DEPTH_METRIC],
    [UnitKeyword.MILLIMETER, UnitPrecision.DEPTH_METRIC],
    [UnitKeyword.MILLIMETERS, UnitPrecision.DEPTH_METRIC],

    [UnitKeyword.IN, UnitPrecision.DEPTH_US],
    [UnitKeyword.INCH, UnitPrecision.DEPTH_US],
    [UnitKeyword.INCHES, UnitPrecision.DEPTH_US],

    [UnitKeyword.FT, UnitPrecision.DISTANCE_US],
    [UnitKeyword.FOOT, UnitPrecision.DISTANCE_US],
    [UnitKeyword.FEET, UnitPrecision.DISTANCE_US],

    [UnitKeyword.CF, UnitPrecision.VOLUME_CUBIC_FEET],
    [UnitKeyword.FT3, UnitPrecision.VOLUME_CUBIC_FEET],
    [UnitKeyword.CUBIC_FEET, UnitPrecision.VOLUME_CUBIC_FEET],

    [UnitKeyword.MG, UnitPrecision.VOLUME_MILLION_GALLONS],
    [UnitKeyword.MILLION_GALLONS, UnitPrecision.VOLUME_MILLION_GALLONS],
    [UnitKeyword.MIL_GAL, UnitPrecision.VOLUME_MILLION_GALLONS],

    [UnitKeyword.M_PER_S, UnitPrecision.VELOCITY],
    [UnitKeyword.MPS, UnitPrecision.VELOCITY],
    [UnitKeyword.METERS_PER_SECOND, UnitPrecision.VELOCITY],

    [UnitKeyword.FT_PER_S, UnitPrecision.VELOCITY],
    [UnitKeyword.FPS, UnitPrecision.VELOCITY],
    [UnitKeyword.FEET_PER_SECOND, UnitPrecision.VELOCITY],

    [UnitKeyword.L_PER_S, UnitPrecision.FLOW_METRIC],
    [UnitKeyword.LPS, UnitPrecision.FLOW_METRIC],
    [UnitKeyword.LITERS_PER_SECOND, UnitPrecision.FLOW_METRIC],

    [UnitKeyword.CFS, UnitPrecision.FLOW_US],
    [UnitKeyword.CUBIC_FEET_PER_SECOND, UnitPrecision.FLOW_US],
    [UnitKeyword.CUFT_PER_S, UnitPrecision.FLOW_US],

    [UnitKeyword.MGD, UnitPrecision.FLOW_US],
    [UnitKeyword.MILLION_GALLONS_PER_DAY, UnitPrecision.FLOW_US],
    [UnitKeyword.MIL_GAL_PER_DAY, UnitPrecision.FLOW_US],
]);

export function getPrecisionByUnitType(unitType: string | undefined): number | undefined {
    if (!unitType || unitType === NONE_UNIT_VALUE) {
        return undefined;
    }

    const normalizedUnit = unitType.toLowerCase().replace(/\s+/g, '');
    return UNIT_PRECISION_MAP.get(normalizedUnit);
}

/**
 * Numeric data types that support MinLength to Min conversion
 */
export const NUMERIC_DATA_TYPES = [DynamicDataType.UInt16, DynamicDataType.Float, DynamicDataType.Float2Byte];

/**
 * Interface for extracted range validation values
 */
interface RangeValues {
    minValue?: number;
    maxValue?: number;
}

/**
 * Generates a standardized field ID from setting properties
 * Following the pattern: displayGroup-settingName (kebab-case)
 *
 * @param setting The dynamic setting to generate ID for
 * @returns Standardized field ID string
 */
export function generateFieldId(setting: DynamicSetting): string {
    return `${setting.displayGroupName.toLowerCase().replace(/\s+/g, '-')}-${setting.name
        .toLowerCase()
        .replace(/[\/\s]+/g, '-')}`;
}

/**
 * Extracts range validation values from validation rules array
 *
 * @param validationRules Array of validation rules to extract from
 * @returns Object containing minValue and maxValue if found
 */
export function extractRangeValues(validationRules: DynamicValidationRule[] | undefined): RangeValues {
    if (!validationRules?.length) {
        return {};
    }

    let minValue: number | undefined;
    let maxValue: number | undefined;

    for (const rule of validationRules) {
        if (rule.ruleType === DynamicValidationRuleType.Range) {
            const rangeRule = rule as RangeValidationRule;
            if (rangeRule.minValue !== undefined) {
                minValue = rangeRule.minValue;
            }
            if (rangeRule.maxValue !== undefined) {
                maxValue = rangeRule.maxValue;
            }
        }
    }

    return { minValue, maxValue };
}

/**
 * Extracts precision value from validation rules with unit-based fallback
 * Priority: API Precision rule > Unit-based lookup > undefined
 */
export function extractPrecision(
    validationRules: DynamicValidationRule[] | undefined,
    unitType?: string,
): number | undefined {
    if (validationRules?.length) {
        const precisionRule = validationRules.find(
            (rule) => rule.ruleType === DynamicValidationRuleType.Precision,
        ) as PrecisionValidationRule;

        if (precisionRule?.precision !== undefined) {
            return precisionRule.precision;
        }
    }

    return getPrecisionByUnitType(unitType);
}

/**
 * Calculates the maximum allowed value for Float2Byte based on precision
 * to prevent overflow when converting to UInt16
 *
 * @param precision Number of decimal places
 * @returns Maximum allowed value
 */
export function calculateFloat2ByteMaxValue(precision: number): number {
    return UINT16_MAX_VALUE / Math.pow(10, precision);
}

/**
 * Creates range validators for numeric fields
 * Handles both individual and paired min/max validators with consistent messaging
 *
 * @param minValue Minimum allowed value
 * @param maxValue Maximum allowed value
 * @param unitDisplay Unit display string (e.g., " cm", " °C")
 * @param maxAllowedValue Optional override for maximum value (used for precision constraints)
 * @param minLengthAsMin Optional min value from MinLength rule conversion
 * @returns Array of validation rules
 */
export function createRangeValidators(
    minValue?: number,
    maxValue?: number,
    unitDisplay: string = '',
    maxAllowedValue?: number,
    minLengthAsMin?: number,
    prec?: number,
): ValidationRule[] {
    const validators: ValidationRule[] = [];
    const effectiveMax =
        maxAllowedValue && maxValue ? Math.min(maxValue, maxAllowedValue) : maxValue ?? maxAllowedValue;
    
    // Use MinLength converted value if available, otherwise use range minValue
    const effectiveMin = minLengthAsMin !== undefined ? minLengthAsMin : minValue;

    // Create paired validators with consistent messaging when both min and max are defined
    if (effectiveMin !== undefined && effectiveMax !== undefined) {

        const message = prec === 1 ? 'LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_AND_PRECISION_METRIC_ERROR' :
        prec && prec > 1 ?  'LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_AND_PRECISION_NON_METRIC_ERROR' : 'LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_BETWEEN'
        const rangeMessage = createParameterizedMessage(message, {
            min: effectiveMin,
            max: effectiveMax,
            unit: unitDisplay
        });
        validators.push(
            min({
                value: effectiveMin,
                message: rangeMessage,
            }),
            max({
                value: effectiveMax,
                message: rangeMessage,
            })
        );
        if(prec != undefined){
            validators.push(
                createPrecisionValidatorWithRanges(effectiveMin, effectiveMax, prec));
        }
    } else {
        // Create individual validators with appropriate messaging
        if (effectiveMin !== undefined) {
            validators.push(
                min({
                    value: effectiveMin,
                    message: createParameterizedMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_MIN', {
                        min: effectiveMin,
                        unit: unitDisplay
                    }),
                }),
            );
        }
        if (effectiveMax !== undefined) {
            validators.push(
                max({
                    value: effectiveMax,
                    message: createParameterizedMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_MAX', {
                        max: effectiveMax,
                        unit: unitDisplay
                    }),
                }),
            );
        }
    }

    return validators;
}

/**
 * Creates a combined range‑and‑precision pattern validator for Float2Byte fields.
 * Validates that the numeric value:
 *  - Falls within the specified min and max range (single‑digit integer range, e.g., 0–9)
 *  - Contains up to the specified number of decimal places
 *  - May include any number of leading zeros (e.g., 01, 001, 0009.5)
 *
 * @param minValue Minimum allowed numeric value
 * @param maxValue Maximum allowed numeric value
 * @param precision Maximum number of decimal places allowed
 * @returns Pattern validation rule with a unified error message
 */
export function createRangePrecisionCombinedPattern(
    minValue: number,
    maxValue: number,
    precision: number
): ValidationRule {

    const unifiedMessage = createParameterizedMessage(
        'LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_AND_PRECISION',
        { min: minValue, max: maxValue, precision }
    );

    const regex = new RegExp(
        `^(?:0*(?:[${minValue}-${Math.floor(maxValue)}](?:\\.\\d{1,${precision}})?))$`
    );

    return pattern({
        value: regex,
        message: unifiedMessage
    });
}



/**
 * Creates a precision pattern validator for Float2Byte fields
 * Validates that input doesn't exceed the specified number of decimal places
 *
 * @param precision Maximum number of decimal places allowed
 * @returns Pattern validation rule
 */
export function createPrecisionValidator(precision: number): ValidationRule {
    const precisionPattern = createPrecisionPattern(precision);
    return pattern({
        value: precisionPattern,
        message: createParameterizedMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.PRECISION', {
            precision: precision
        }),
    });
}


/**
 * Creates a precision-only regex validator that supports both positive and negative numbers,
 * while sharing a unified range‑and‑precision error message with the Min/Max validators.
 *
 * @param min The minimum allowed numeric value for the field.
 * @param max The maximum allowed numeric value for the field. 
 * @param precision The maximum number of decimal places allowed.
 */
export function createPrecisionValidatorWithRanges(min: number, max: number, precision: number): ValidationRule {
    const precisionPattern = createPrecisionPattern(precision);
    const message = precision && precision === 1 ? 'LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_AND_PRECISION_METRIC_ERROR' : 'LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_AND_PRECISION_NON_METRIC_ERROR'
    return pattern({
        value: precisionPattern,
        message: createParameterizedMessage(message, {
            min: min,
            max: max,
        }),
    });
}

function createPrecisionPattern(precision: number): RegExp {
    return precision === 0 ? /^-?\d+$/ : new RegExp(`^-?(?:\\d+|\\.\\d{1,${precision}}|\\d+\\.\\d{1,${precision}})$`);
}

export function convertToNumeric(currentValue: unknown): number | null {
    return typeof currentValue === 'number' ? currentValue : Number(currentValue);
}

/**
 * Creates a parameterized translation message for use with TranslateSmartPipe
 * This allows the pipe to handle translations with parameters like min/max values
 *
 * @param key The translation key (e.g., 'LOCATION_CARD_DYNAMIC_TRANSLATION.RANGE_BETWEEN')
 * @param params Object containing parameters for the translation
 * @returns JSON string that the TranslateSmartPipe can parse and process
 */
export function createParameterizedMessage(key: string, params: { [key: string]: number | string | boolean }): string {
    return JSON.stringify({ key, params });
}

/**
 * Extracts DisplayValueList validation rule from validation rules array
 *
 * @param validationRules Array of validation rules to extract from
 * @returns DisplayValueList rule if found, undefined otherwise
 */
export function extractDisplayValueList(validationRules: DynamicValidationRule[] | undefined): DisplayValueListValidationRule | undefined {
    if (!validationRules?.length) {
        return undefined;
    }

    return validationRules.find(
        (rule) => rule.ruleType === DynamicValidationRuleType.DisplayValueList,
    ) as DisplayValueListValidationRule;
}

/**
 * Converts DisplayValueList values to select options
 *
 * @param displayValueRule The DisplayValueList validation rule
 * @returns Array of select options for dropdown
 *
 * DisplayValueList structure: { "KEY": "VALUE" }
 * - KEY: What gets sent to API (always)
 * - VALUE: Display label (if null, show KEY instead)
 */
export function convertDisplayValueListToOptions(displayValueRule: DisplayValueListValidationRule): Array<{ text: string; value: string | number }> {
    return Object.entries(displayValueRule.values)
        .map(([key, value]) => {
            const displayText = value != null ? String(value) : key;
            const numericKey = Number(key);
            const optionValue = !isNaN(numericKey) && isFinite(numericKey) ? numericKey : key;

            return {
                text: displayText,
                value: optionValue,
            };
        })
        .sort((a: { text: string; value: string | number }, b: { text: string; value: string | number }) => {
            const aIsNumber = typeof a.value === 'number';
            const bIsNumber = typeof b.value === 'number';

            if (aIsNumber && bIsNumber) {
                return (a.value as number) - (b.value as number);
            }
            if (aIsNumber) return -1;
            if (bIsNumber) return 1;
            return String(a.value).localeCompare(String(b.value));
        });
}

/**
 * Checks if MinLength rule should be converted to Min for numeric fields
 *
 * @param validationRules Array of validation rules
 * @param dataType The data type of the field
 * @returns Min value if conversion needed, undefined otherwise
 */
export function extractMinValueFromMinLength(validationRules: DynamicValidationRule[] | undefined, dataType: string): number | undefined {
    if (!validationRules?.length || !NUMERIC_DATA_TYPES.includes(dataType as DynamicDataType)) {
        return undefined;
    }

    const minLengthRule = validationRules.find(
        (rule) => rule.ruleType === DynamicValidationRuleType.MinLength,
    ) as { value: number } | undefined;

    return minLengthRule?.value;
}

